import { normalizeVoiceTranscriptionInputProvenance } from '@/lib/pieveInputProvenance.js';
import { normalizeThreadSandboxMode } from '@/lib/pieveComposerSettings.js';

const toOptionalRuntimeString = (value) => {
  const normalized = String(value || '').trim();
  return normalized || null;
};

const toOptionalRuntimeStringArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      return toOptionalRuntimeString(entry);
    })
    .filter(Boolean);
};

const resolveRuntimeProjectDocPayload = ({ runtimeObject }) => {
  const safeRuntimeObject = runtimeObject && typeof runtimeObject === 'object' && !Array.isArray(runtimeObject) ? runtimeObject : {};
  const runtimeProjectDoc = safeRuntimeObject?.projectDoc && typeof safeRuntimeObject.projectDoc === 'object' && !Array.isArray(safeRuntimeObject.projectDoc) ? safeRuntimeObject.projectDoc : {};
  const instruction = toOptionalRuntimeString(safeRuntimeObject?.projectDocInstruction) || toOptionalRuntimeString(runtimeProjectDoc?.instruction);

  if (!instruction) {
    return {};
  }

  const directoryLabel = toOptionalRuntimeString(runtimeProjectDoc?.directoryLabel) || toOptionalRuntimeString(safeRuntimeObject?.projectDocDirectoryLabel);
  const discoveredPaths = toOptionalRuntimeStringArray(runtimeProjectDoc?.discoveredPaths);

  return {
    projectDocInstruction: instruction,
    projectDoc: {
      instruction,
      ...(directoryLabel ? { directoryLabel } : {}),
      ...(discoveredPaths.length > 0 ? { discoveredPaths } : {})
    }
  };
};

const getBrowserLocale = () => {
  const localeFromNavigator = toOptionalRuntimeString(globalThis?.navigator?.language);

  if (localeFromNavigator) {
    return localeFromNavigator;
  }

  return toOptionalRuntimeString(Intl?.DateTimeFormat?.().resolvedOptions?.().locale);
};

const getBrowserTimezone = () => {
  return toOptionalRuntimeString(Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone);
};

const resolveRuntimeProjectContext = ({ thread, projectId = null, projectName = null, projectWorkingDir = null, projectFolderName = null, sandboxMode = null } = {}) => {
  const resolvedProjectId = toOptionalRuntimeString(projectId) || toOptionalRuntimeString(thread?.projectId) || toOptionalRuntimeString(thread?.project?.id) || toOptionalRuntimeString(thread?.project?.projectId);
  const resolvedProjectName = toOptionalRuntimeString(projectName) || toOptionalRuntimeString(thread?.projectName) || toOptionalRuntimeString(thread?.project?.name) || toOptionalRuntimeString(thread?.project?.title) || toOptionalRuntimeString(thread?.project?.label);
  const resolvedProjectWorkingDir = toOptionalRuntimeString(projectWorkingDir) || toOptionalRuntimeString(thread?.projectWorkingDir) || toOptionalRuntimeString(thread?.project?.workingDir) || toOptionalRuntimeString(thread?.project?.rootPath) || toOptionalRuntimeString(thread?.project?.path) || toOptionalRuntimeString(thread?.worktreePath) || toOptionalRuntimeString(thread?.repoPath) || toOptionalRuntimeString(thread?.project?.worktreePath) || toOptionalRuntimeString(thread?.project?.repoPath);
  const resolvedProjectFolderName = toOptionalRuntimeString(projectFolderName) || toOptionalRuntimeString(thread?.projectFolderName) || toOptionalRuntimeString(thread?.project?.folderName);
  const resolvedSandboxMode = normalizeThreadSandboxMode(sandboxMode || thread?.sandboxMode, '');

  return {
    projectId: resolvedProjectId,
    projectName: resolvedProjectName,
    projectWorkingDir: resolvedProjectWorkingDir,
    projectFolderName: resolvedProjectFolderName,
    sandboxMode: resolvedSandboxMode || null
  };
};

const getWebRuntimeContext = ({ knowledgeId = null, projectId = null, projectName = null, projectWorkingDir = null, projectFolderName = null, sandboxMode = null, locale = null, timezone = null, runtime = null, inputProvenance = null } = {}) => {
  const resolvedKnowledgeId = String(knowledgeId || '').trim() || null;
  const resolvedProjectId = toOptionalRuntimeString(projectId);
  const resolvedProjectName = toOptionalRuntimeString(projectName);
  const resolvedProjectWorkingDir = toOptionalRuntimeString(projectWorkingDir);
  const resolvedProjectFolderName = toOptionalRuntimeString(projectFolderName);
  const resolvedLocale = toOptionalRuntimeString(locale) || getBrowserLocale();
  const resolvedTimezone = toOptionalRuntimeString(timezone) || getBrowserTimezone();
  const runtimeObject = runtime && typeof runtime === 'object' && !Array.isArray(runtime) ? runtime : {};
  const resolvedPreferredResponseLanguage = toOptionalRuntimeString(runtimeObject?.preferredResponseLanguage);
  /* The companion thread's location envelope (2026-08-20): this builder is a deliberate
     whitelist, so a caller-supplied runtime key crosses the wire only by being named here —
     `location` was silently dropped on the companion's first live run. */
  const resolvedLocation = runtimeObject?.location && typeof runtimeObject.location === 'object' && !Array.isArray(runtimeObject.location) ? runtimeObject.location : null;
  const resolvedSandboxMode = normalizeThreadSandboxMode(sandboxMode || runtimeObject?.executionSandboxMode || runtimeObject?.execution?.sandboxMode || runtimeObject?.sandboxMode, '');
  const runtimeProjectDocPayload = resolveRuntimeProjectDocPayload({ runtimeObject });
  const normalizedInputProvenance = normalizeVoiceTranscriptionInputProvenance({
    inputProvenance: inputProvenance || runtimeObject?.inputProvenance
  });

  return {
    kind: 'web',
    platform: 'web',
    workspaceType: 'shared',
    workspaceRoot: '/',
    knowledgeId: resolvedKnowledgeId,
    capabilities: {
      isDesktop: false,
      canStartRun: true,
      canCancelRun: true,
      canSetSandboxMode: true,
      canPickRepo: false,
      canRunShell: false,
      canReadFile: true,
      canRunSearch: true,
      canRunAuditBundleReveal: false,
      canCopyRunBundlePath: false,
      canRespondToApprovals: true,
      canRespondToInteractions: true,
      canReadSharedWorkspaces: true,
      canControlSharedSync: false,
      canWindowDrag: false
    },
    projectId: resolvedProjectId,
    projectName: resolvedProjectName,
    projectWorkingDir: resolvedProjectWorkingDir,
    projectFolderName: resolvedProjectFolderName,
    ...(resolvedSandboxMode
      ? {
          sandboxMode: resolvedSandboxMode,
          executionSandboxMode: resolvedSandboxMode,
          execution: {
            sandboxMode: resolvedSandboxMode
          }
        }
      : {}),
    locale: resolvedLocale,
    timezone: resolvedTimezone,
    ...(resolvedPreferredResponseLanguage
      ? {
          preferredResponseLanguage: resolvedPreferredResponseLanguage
        }
      : {}),
    ...(resolvedLocation
      ? {
          location: resolvedLocation
        }
      : {}),
    ...(Object.hasOwn(runtimeObject, 'analyticsTableEditor') ? { analyticsTableEditor: runtimeObject.analyticsTableEditor } : {}),
    /* The page tab on screen for a page orchestrator run (sendPromptToDesktop stamps it). */
    ...(Object.hasOwn(runtimeObject, 'analyticsPageView') ? { analyticsPageView: runtimeObject.analyticsPageView } : {}),
    userLocation: resolvedTimezone
      ? {
          timezone: resolvedTimezone
        }
      : null,
    ...(normalizedInputProvenance
      ? {
          inputProvenance: normalizedInputProvenance
        }
      : {}),
    ...runtimeProjectDocPayload,
    project:
      resolvedProjectId || resolvedProjectName || resolvedProjectWorkingDir || resolvedProjectFolderName
        ? {
            ...(resolvedProjectId ? { id: resolvedProjectId } : {}),
            ...(resolvedProjectName ? { name: resolvedProjectName } : {}),
            ...(resolvedProjectWorkingDir ? { workingDir: resolvedProjectWorkingDir, rootPath: resolvedProjectWorkingDir } : {}),
            ...(resolvedProjectFolderName ? { folderName: resolvedProjectFolderName } : {})
          }
        : null
  };
};

export { getWebRuntimeContext, resolveRuntimeProjectContext, toOptionalRuntimeString };
